import {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {CartService} from '../services/CartService';
import { useComercialContext } from '../../comercial/ComercialContext';

const findGiftProduct = (cart, productId) => {
  // Solo el obsequio de promo que postea el front: las líneas de pack (packOrigin)
  // las genera y gestiona el servidor y no hay que borrarlas ni cascadearlas aquí.
  const isGiftProduct = ({ packParentId, packOrigin }) => packParentId === productId && !packOrigin;
  return cart.products.find(isGiftProduct) || cart.deprecatedProducts.find(isGiftProduct);
};

const CartContext = createContext();

export const CartContextProvider = ({ children }) => {
  const { actingAsCliente } = useComercialContext();
  const [cart, setCart] = useState();

  const [equivalenceChargedProducts, setEquivalenceChargedProducts] = useState();
  const [equivalenceChargeFreeProducts, setEquivalenceChargeFreeProducts] = useState();

  const [loading, setLoading] = useState(true);

  const loadCart = useCallback(async () => {
    setLoading(true);
    try {
      setCart(await CartService.get());
    } finally {
      setLoading(false);
    }
  }, []);

  const changeEquivalenceCharges = useCallback(async (product) => {
    await CartService.changeEquivalenceCharges(product);
    const updatedCart = await CartService.get();

    const giftProduct = findGiftProduct(updatedCart, product.id);
    if (giftProduct) {
      await CartService.changeEquivalenceCharges(giftProduct);
    }

    await loadCart();
  }, [loadCart]);

  // Datos de receta (modo comercial): el servidor devuelve el carrito ya actualizado.
  const updateReceta = useCallback(async (product, receta) => {
    setCart(await CartService.updateReceta(product, receta));
  }, []);

  const loadEspecies = useCallback((product) => CartService.getEspecies(product), []);

  // Precio manual del comercial: el servidor devuelve el carrito ya recalculado.
  const updatePrecioManual = useCallback(async (product, precio) => {
    setCart(await CartService.updatePrecioManual(product, precio));
  }, []);

  const removeProduct = useCallback(async (product) => {
    return CartService.removeProduct(product).then(cart => {
      setCart(cart);
      return cart;
    });
  }, []);

  const updateProductQuantity = useCallback(async (product, quantity) => {
    return CartService.updateProductQuantity(product, quantity).then(cart => {
      setCart(cart);
      return cart;
    });
  }, []);

  const addProduct = useCallback(async (productSelection) => {
    const actingAsNumCliente = actingAsCliente?.numCliente ?? null;
    let updatedCart = await CartService.addProductBis({
      productId: productSelection.id,
      quantity: productSelection.quantity,
      price: productSelection.price,
      bcDiscounts: productSelection.bcDiscounts,
      actingAsNumCliente
    });

    const giftProduct = findGiftProduct(updatedCart, productSelection.id);
    if (giftProduct) {
      updatedCart = await CartService.removeProduct(giftProduct);
    }

    if (!!productSelection.giftProduct) {
      updatedCart = await CartService.addProductBis({
        productId: productSelection.giftProduct.id,
        quantity: productSelection.giftProduct.quantity,
        price: productSelection.giftProduct.price,
        bcDiscounts: productSelection.giftProduct.bcDiscounts,
        packParentId: productSelection.id,
        actingAsNumCliente
      });
    }

    setCart(updatedCart);
    return updatedCart;
  }, [actingAsCliente]);

  const loadCartBis = useCallback(async () => {
    const loadedCart = await CartService.get();
    setCart(loadedCart);
    return loadedCart;
  }, []);

  const isOnCart = useCallback(productId => {
      const products = cart?.products ?? [];
      const deprecatedProducts = cart?.deprecatedProducts ?? [];
      return [...products, ...deprecatedProducts].some(it => it?.id === productId);
    },
    [cart]);

  useEffect(() => {
    setEquivalenceChargedProducts(cart?.products?.filter(product => product.equivalenceCharge === true));
    setEquivalenceChargeFreeProducts(cart?.products?.filter(product => product.equivalenceCharge === false));
  }, [cart]);

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      loadCart,
      changeEquivalenceCharges,
      updateReceta,
      loadEspecies,
      updatePrecioManual,
      equivalenceChargeFreeProducts,
      equivalenceChargedProducts,
      removeProduct,
      updateProductQuantity,
      addProduct,
      loadCartBis,
      isOnCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCartContext = () => useContext(CartContext);
